import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  Brand,
  Category,
  ItemCondition,
  ItemFull,
  ItemGender,
  ItemStatus,
  Occasion
} from '@lr/shared';
import { apiFetch } from '../../lib/api';
import { uploadProductImage } from '../../lib/upload';
import { COLOURS } from '../../lib/colours';

interface FormState {
  name: string;
  slug: string;
  description: string;
  label_size: string;
  recommended_size: string;
  pit_to_pit_cm: string;
  shoulder_width_cm: string;
  length_cm: string;
  damage_fee_pence: string;
  condition: ItemCondition;
  credit_price: number;
  brand_id: string;
  category_id: string;
  gender: ItemGender;
  status: ItemStatus;
  featured: boolean;
  main_image_url: string;
  colours: string[];
  occasion_ids: string[];
  images: { url: string; alt_text: string | null }[];
}

const empty: FormState = {
  name: '',
  slug: '',
  description: '',
  label_size: '',
  recommended_size: '',
  pit_to_pit_cm: '',
  shoulder_width_cm: '',
  length_cm: '',
  damage_fee_pence: '0',
  condition: 'excellent',
  credit_price: 1,
  brand_id: '',
  category_id: '',
  gender: 'male',
  status: 'draft',
  featured: false,
  main_image_url: '',
  colours: [],
  occasion_ids: [],
  images: []
};

export function AdminItemEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState<FormState>(empty);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState<'brand' | 'category' | 'occasion' | null>(null);

  // Reload reference data; called once on mount and after inline creates.
  function reloadRefs() {
    void Promise.all([
      apiFetch<Brand[]>('/api/admin/brands').then(setBrands),
      apiFetch<Category[]>('/api/admin/categories').then(setCategories),
      apiFetch<Occasion[]>('/api/admin/occasions').then(setOccasions)
    ]);
  }
  useEffect(() => reloadRefs(), []);

  useEffect(() => {
    if (isNew) return;
    apiFetch<ItemFull & { occasions: Occasion[] }>(`/api/admin/items/${id}`)
      .then((i) => {
        setForm({
          name: i.name,
          slug: i.slug,
          description: i.description ?? '',
          label_size: i.label_size ?? '',
          recommended_size: i.recommended_size ?? '',
          pit_to_pit_cm: i.pit_to_pit_cm?.toString() ?? '',
          shoulder_width_cm: i.shoulder_width_cm?.toString() ?? '',
          length_cm: i.length_cm?.toString() ?? '',
          damage_fee_pence: String(i.damage_fee_pence ?? 0),
          condition: i.condition,
          credit_price: i.credit_price,
          brand_id: i.brand_id,
          category_id: i.category_id,
          gender: i.gender,
          status: i.status,
          featured: !!i.featured_at,
          main_image_url: i.main_image_url ?? '',
          colours: i.colours ?? [],
          occasion_ids: i.occasions?.map((o) => o.id) ?? [],
          images: (i.images ?? [])
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((img) => ({ url: img.url, alt_text: img.alt_text }))
        });
      })
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleColour(slug: string) {
    set(
      'colours',
      form.colours.includes(slug)
        ? form.colours.filter((c) => c !== slug)
        : [...form.colours, slug]
    );
  }

  function toggleOccasion(occasionId: string) {
    set(
      'occasion_ids',
      form.occasion_ids.includes(occasionId)
        ? form.occasion_ids.filter((o) => o !== occasionId)
        : [...form.occasion_ids, occasionId]
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        description: form.description || null,
        label_size: form.label_size || null,
        recommended_size: form.recommended_size || null,
        pit_to_pit_cm: form.pit_to_pit_cm ? Number(form.pit_to_pit_cm) : null,
        shoulder_width_cm: form.shoulder_width_cm ? Number(form.shoulder_width_cm) : null,
        length_cm: form.length_cm ? Number(form.length_cm) : null,
        damage_fee_pence: Number(form.damage_fee_pence) || 0,
        condition: form.condition,
        credit_price: form.credit_price,
        brand_id: form.brand_id,
        category_id: form.category_id,
        gender: form.gender,
        status: form.status,
        featured: form.featured,
        main_image_url: form.main_image_url || null,
        colours: form.colours,
        occasion_ids: form.occasion_ids,
        images: form.images
      };
      if (isNew) {
        const created = await apiFetch<{ id: string }>('/api/admin/items', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        navigate(`/admin/items/${created.id}`);
      } else {
        await apiFetch(`/api/admin/items/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        navigate('/admin/items');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ color: 'var(--a-ink-soft)', padding: 16 }}>Loading…</div>;
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="admin-breadcrumb">
        <Link to="/admin/items">Items</Link>
        <span className="sep">/</span>
        <span>{isNew ? 'New item' : form.name || 'Edit item'}</span>
      </div>
      <div className="admin-page-head">
        <div>
          <h1>{isNew ? 'Add new item' : form.name || 'Edit item'}</h1>
          <div className="sub">
            {isNew ? 'Create a piece for the rotation.' : `Editing ${form.slug || 'item'}`}
          </div>
        </div>
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}

      <div className="a-detail-grid">
        <div style={{ display: 'grid', gap: 18 }}>
          {/* BASICS */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Basics</h2>
            </div>
            <div className="a-card-body">
              <div className="a-form">
                <div className="a-form-grid">
                  <Field label="Name" required>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => set('name', e.target.value)}
                      required
                    />
                  </Field>
                  <Field
                    label="Slug"
                    help="Auto-generated from the name if left blank."
                  >
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => set('slug', e.target.value)}
                      placeholder="e.g. carhartt-detroit-jacket"
                    />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    rows={3}
                    placeholder="What's special about this piece?"
                  />
                </Field>

                <div className="a-form-grid">
                  <Field label="Brand" required>
                    <Combo
                      value={form.brand_id}
                      onChange={(v) => set('brand_id', v)}
                      options={brands.map((b) => ({ value: b.id, label: b.name }))}
                      onAdd={() => setShowCreate('brand')}
                      addLabel="+ New"
                    />
                  </Field>
                  <Field label="Category" required>
                    <Combo
                      value={form.category_id}
                      onChange={(v) => set('category_id', v)}
                      options={categories.map((c) => ({ value: c.id, label: c.name }))}
                      onAdd={() => setShowCreate('category')}
                      addLabel="+ New"
                    />
                  </Field>
                </div>

                <div className="a-form-grid-3">
                  <Field label="Gender">
                    <select
                      value={form.gender}
                      onChange={(e) => set('gender', e.target.value as ItemGender)}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="unisex">Unisex</option>
                    </select>
                  </Field>
                  <Field label="Condition">
                    <select
                      value={form.condition}
                      onChange={(e) => set('condition', e.target.value as ItemCondition)}
                    >
                      <option value="preloved">Preloved</option>
                      <option value="good">Good</option>
                      <option value="very_good">Very good</option>
                      <option value="excellent">Excellent</option>
                    </select>
                  </Field>
                  <Field
                    label="Status"
                    help={
                      form.status === 'reserved'
                        ? 'Stuck in someone’s basket. Set to Available to release.'
                        : form.status === 'rented_out'
                          ? 'Out on an active rental.'
                          : undefined
                    }
                  >
                    <select
                      value={form.status}
                      onChange={(e) => set('status', e.target.value as ItemStatus)}
                    >
                      <option value="draft">Draft</option>
                      <option value="coming_soon">Coming soon</option>
                      <option value="available">Available (live)</option>
                      <option value="reserved">Reserved (in a basket)</option>
                      <option value="rented_out">Rented out</option>
                      <option value="returning">Returning</option>
                      <option value="archived">Archived</option>
                    </select>
                  </Field>
                </div>

                {/* Feature toggle — pins this item to the top of Browse.
                    Hidden when status would prevent it from being shown
                    to customers anyway. */}
                <div style={{ marginTop: 12 }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: 'pointer',
                      padding: '12px 14px',
                      background: form.featured
                        ? 'var(--a-ink, #0e0e0e)'
                        : 'var(--a-bg-soft, #f7f4ec)',
                      color: form.featured ? '#fff' : 'var(--a-ink, #0e0e0e)',
                      borderRadius: 8,
                      border: '1px solid var(--a-line, #e6e0d2)',
                      transition: 'all 0.15s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.featured}
                      onChange={(e) => set('featured', e.target.checked)}
                      style={{ width: 18, height: 18 }}
                    />
                    <span style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {form.featured ? '★ Featured' : '☆ Feature on Browse'}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          opacity: 0.8,
                          marginTop: 2
                        }}
                      >
                        {form.featured
                          ? 'Pinned to the top of Browse. Untick to remove.'
                          : 'Pin to the top of Browse to push this piece.'}
                      </div>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* IMAGES */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Photography</h2>
              <span style={{ fontSize: 11, color: 'var(--a-muted)' }}>
                Main image + up to 5 gallery shots
              </span>
            </div>
            <div className="a-card-body">
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 8,
                    color: 'var(--a-ink)'
                  }}
                >
                  Main image
                </div>
                <div style={{ maxWidth: 240 }}>
                  <ImageUploadTile
                    url={form.main_image_url}
                    onChange={(url) => set('main_image_url', url ?? '')}
                    itemId={isNew ? undefined : id}
                  />
                </div>
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: 'var(--a-ink)'
                }}
              >
                Gallery
              </div>
              <div className="gallery-grid">
                {form.images.map((img, idx) => (
                  <ImageUploadTile
                    key={idx}
                    url={img.url}
                    onChange={(url) => {
                      if (!url) {
                        set(
                          'images',
                          form.images.filter((_, i) => i !== idx)
                        );
                      } else {
                        const next = [...form.images];
                        next[idx] = { ...next[idx], url };
                        set('images', next);
                      }
                    }}
                    itemId={isNew ? undefined : id}
                  />
                ))}
                {form.images.length < 5 && (
                  <ImageUploadTile
                    onChange={(url) => {
                      if (url) set('images', [...form.images, { url, alt_text: null }]);
                    }}
                    itemId={isNew ? undefined : id}
                    placeholderText="+ Add gallery image"
                  />
                )}
              </div>
            </div>
          </div>

          {/* SIZE & FIT */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Size &amp; fit</h2>
            </div>
            <div className="a-card-body">
              <div className="a-form-grid">
                <Field label="Label size" help="What's printed on the tag (e.g. M, 32R, 9 UK).">
                  <input
                    type="text"
                    value={form.label_size}
                    onChange={(e) => set('label_size', e.target.value)}
                  />
                </Field>
                <Field label="Recommended size" help="What it fits like in modern sizing.">
                  <input
                    type="text"
                    value={form.recommended_size}
                    onChange={(e) => set('recommended_size', e.target.value)}
                  />
                </Field>
              </div>
              <div className="a-form-grid-3" style={{ marginTop: 14 }}>
                <Field label="Pit to pit (cm)">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={form.pit_to_pit_cm}
                    onChange={(e) => set('pit_to_pit_cm', e.target.value)}
                  />
                </Field>
                <Field label="Shoulder (cm)">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={form.shoulder_width_cm}
                    onChange={(e) => set('shoulder_width_cm', e.target.value)}
                  />
                </Field>
                <Field label="Length (cm)">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={form.length_cm}
                    onChange={(e) => set('length_cm', e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* COLOURS & OCCASIONS */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Colours</h2>
              <span style={{ fontSize: 11, color: 'var(--a-muted)' }}>
                {form.colours.length} selected
              </span>
            </div>
            <div className="a-card-body">
              <div className="swatch-grid">
                {COLOURS.map((c) => {
                  const selected = form.colours.includes(c.slug);
                  const isPattern = c.hex.includes('gradient');
                  return (
                    <button
                      type="button"
                      key={c.slug}
                      className={`swatch${selected ? ' selected' : ''}`}
                      onClick={() => toggleColour(c.slug)}
                    >
                      <span
                        className="dot"
                        style={isPattern ? { background: c.hex } : { background: c.hex }}
                      />
                      <span>{c.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="a-card">
            <div className="a-card-head">
              <h2>Occasions</h2>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setShowCreate('occasion')}
              >
                + New occasion
              </button>
            </div>
            <div className="a-card-body">
              {occasions.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--a-ink-soft)' }}>
                  No occasions yet. Add one to start tagging.
                </div>
              ) : (
                <div className="tag-list">
                  {occasions.map((o) => {
                    const selected = form.occasion_ids.includes(o.id);
                    return (
                      <button
                        type="button"
                        key={o.id}
                        className="swatch"
                        style={{
                          gridTemplateColumns: 'auto',
                          padding: '6px 12px',
                          borderColor: selected ? 'var(--a-ink)' : undefined,
                          background: selected ? 'var(--a-accent-bg)' : undefined
                        }}
                        onClick={() => toggleOccasion(o.id)}
                      >
                        {o.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SIDEBAR */}
        <div style={{ display: 'grid', gap: 18, position: 'sticky', top: 80 }}>
          <div className="a-card">
            <div className="a-card-head">
              <h2>Pricing</h2>
            </div>
            <div className="a-card-body">
              <div className="a-form" style={{ gap: 14 }}>
                <Field
                  label="Credit price"
                  help="How many credits the customer spends to rent this piece (1–8)."
                >
                  <div className="amount-input">
                    <input
                      className="amount-field"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={8}
                      value={form.credit_price}
                      onChange={(e) =>
                        set('credit_price', Number(e.target.value) || 1)
                      }
                    />
                    <span className="amount-suffix">credits</span>
                  </div>
                </Field>
                <Field
                  label="Damage fee"
                  help="Charged only if the piece can't be returned to the rotation."
                >
                  <div className="amount-input">
                    <span className="amount-prefix">£</span>
                    <input
                      className="amount-field"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.01"
                      value={
                        form.damage_fee_pence
                          ? (Number(form.damage_fee_pence) / 100).toFixed(2)
                          : ''
                      }
                      onChange={(e) => {
                        const pounds = parseFloat(e.target.value);
                        const pence = Number.isFinite(pounds)
                          ? Math.round(pounds * 100)
                          : 0;
                        set('damage_fee_pence', String(pence));
                      }}
                      placeholder="0.00"
                    />
                  </div>
                </Field>
              </div>
            </div>
          </div>

          <div className="a-card">
            <div className="a-card-head">
              <h2>Save</h2>
            </div>
            <div
              className="a-form-foot"
              style={{ borderTop: 0, flexWrap: 'wrap', gap: 8 }}
            >
              {!isNew && (
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ color: 'var(--a-danger)', marginRight: 'auto' }}
                  onClick={async () => {
                    if (!confirm('Delete this item permanently? This cannot be undone.')) {
                      return;
                    }
                    try {
                      await apiFetch(`/api/admin/items/${id}`, { method: 'DELETE' });
                      navigate('/admin/items');
                    } catch (e) {
                      setError(e instanceof Error ? e.message : 'Delete failed');
                    }
                  }}
                >
                  Delete item
                </button>
              )}
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/admin/items')}
              >
                Cancel
              </button>
              <button type="submit" disabled={saving} className="btn-primary">
                {saving ? 'Saving…' : isNew ? 'Create item' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* INLINE CREATE MODALS */}
      {showCreate === 'brand' && (
        <CreateRefModal
          title="New brand"
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'slug', label: 'Slug', required: true }
          ]}
          endpoint="/api/admin/brands"
          onClose={(created) => {
            setShowCreate(null);
            if (created?.id) {
              reloadRefs();
              set('brand_id', created.id);
            }
          }}
        />
      )}
      {showCreate === 'category' && (
        <CreateRefModal
          title="New category"
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'slug', label: 'Slug', required: true }
          ]}
          endpoint="/api/admin/categories"
          onClose={(created) => {
            setShowCreate(null);
            if (created?.id) {
              reloadRefs();
              set('category_id', created.id);
            }
          }}
        />
      )}
      {showCreate === 'occasion' && (
        <CreateRefModal
          title="New occasion"
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'slug', label: 'Slug', required: true }
          ]}
          endpoint="/api/admin/occasions"
          onClose={(created) => {
            setShowCreate(null);
            if (created?.id) reloadRefs();
          }}
        />
      )}
    </form>
  );
}

function Field({
  label,
  required,
  help,
  children
}: {
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="a-field">
      <label>
        {label} {required && <span style={{ color: 'var(--a-danger)' }}>*</span>}
      </label>
      {children}
      {help && <div className="help">{help}</div>}
    </div>
  );
}

function Combo({
  value,
  onChange,
  options,
  onAdd,
  addLabel
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  onAdd?: () => void;
  addLabel?: string;
}) {
  return (
    <div className="a-combo">
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— Select —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {onAdd && (
        <button type="button" className="add-btn" onClick={onAdd}>
          {addLabel ?? 'Add'}
        </button>
      )}
    </div>
  );
}

function ImageUploadTile({
  url,
  onChange,
  itemId,
  placeholderText = 'Click or drop image'
}: {
  url?: string;
  onChange: (url: string | null) => void;
  itemId?: string;
  placeholderText?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      const publicUrl = await uploadProductImage(file, itemId);
      onChange(publicUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <div
        className={`upload-tile${url ? ' has-image' : ''}${uploading ? ' uploading' : ''}`}
        style={url ? { backgroundImage: `url(${url})` } : undefined}
        onClick={() => !url && !uploading && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (url || uploading) return;
          const f = e.dataTransfer.files[0];
          if (f) void handleFile(f);
        }}
      >
        {!url && !uploading && (
          <>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>{placeholderText}</span>
          </>
        )}
        {uploading && <div className="uploading-label">Uploading…</div>}
        {url && !uploading && (
          <div className="actions">
            <button
              type="button"
              className="ic-btn"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
              aria-label="Replace"
              title="Replace"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
            <button
              type="button"
              className="ic-btn danger"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              aria-label="Remove"
              title="Remove"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <div style={{ fontSize: 11, color: 'var(--a-danger)', marginTop: 4 }}>{error}</div>
      )}
    </div>
  );
}

function CreateRefModal({
  title,
  fields,
  endpoint,
  onClose
}: {
  title: string;
  fields: { key: string; label: string; required?: boolean }[];
  endpoint: string;
  onClose: (created?: { id: string }) => void;
}) {
  const [vals, setVals] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { ...vals };
      // Auto-fill slug from name if missing
      if (body.name && !body.slug) {
        body.slug = String(body.name)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      const created = await apiFetch<{ id: string }>(endpoint, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      onClose(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="a-modal-overlay open" onClick={() => onClose()} />
      <aside className="a-modal open">
        <div className="a-modal-head">
          <h2>{title}</h2>
          <button className="btn-ghost" onClick={() => onClose()}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="a-modal-body">
          {error && <div className="a-alert a-alert-error">{error}</div>}
          <div className="a-form">
            {fields.map((f) => (
              <div className="a-field" key={f.key}>
                <label>
                  {f.label} {f.required && <span style={{ color: 'var(--a-danger)' }}>*</span>}
                </label>
                <input
                  type="text"
                  value={vals[f.key] ?? ''}
                  onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="a-modal-foot">
          <button className="btn-secondary" onClick={() => onClose()}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Create'}
          </button>
        </div>
      </aside>
    </>
  );
}
